import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import Heading from "@tiptap/extension-heading";
import Underline from "@tiptap/extension-underline";
import HardBreak from "@tiptap/extension-hard-break";
import Strike from "@tiptap/extension-strike";
import BulletList from "@tiptap/extension-bullet-list";
import ListItem from "@tiptap/extension-list-item";
import Link from "@tiptap/extension-link";
import { isExplicitUrl } from "src/lib/tipTapExtensions/pasteUrlAsLink";
import Image from "@tiptap/extension-image";
import Typography from "@tiptap/extension-typography";
import classNames from "classnames";
import styled from "styled-components/macro";
import { themeVariables } from "src/themeVariables";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { TooltipButton } from "src/sudokit/components/TooltipButton";
import IconButton from "src/components/IconButton";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip";
import { editorTypographyStyles } from "../EditorContainer/editorContentStyles";
import { createReadOnlyPlaceholder } from "../../../lib/tipTapExtensions/readOnlyPlaceholder";
import { createTaskListExtensions } from "../../../lib/tipTapExtensions/taskListExtensions";

const extensions = [
  Document,
  Paragraph,
  Text,
  Bold,
  Italic,
  Heading,
  Underline,
  HardBreak,
  Strike,
  BulletList,
  ListItem,
  ...createTaskListExtensions({ canWrite: false }),
  Link.configure({ openOnClick: false, autolink: true, shouldAutoLink: isExplicitUrl, defaultProtocol: "https" }),
  Image,
  Typography,
  createReadOnlyPlaceholder(),
];

const ComparisonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 20px 20px;
  box-sizing: border-box;

  ${(props) =>
    !props.$visible &&
    `
    position: absolute;
    visibility: hidden;
    height: 0;
    overflow: hidden;
    pointer-events: none;
  `}
`;

const PromptRow = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 0 12px;

  .icon-button-wrapper {
    margin-left: auto;
    color: var(--grey-2);

    svg {
      transform: rotate(-45deg);

      path {
        fill: var(--grey-2);
      }
    }
  }
`;

const PromptMessage = styled.div`
  flex: 1;
  text-align: center;
  font-size: 15px;
  color: var(--grey-2);
  line-height: 1.4;
`;

const TwoWays = styled.span`
  text-decoration: underline;
  text-underline-offset: 4px;
  text-decoration-color: var(--grey-3);
  cursor: pointer;
`;

const ComparisonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
`;

const Panel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid ${themeVariables.divider};
  border-radius: 12px;
  overflow: hidden;
  background: var(--document-background);
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid ${themeVariables.divider};
  background: ${themeVariables.menuColorFullscreen};

  .label {
    font-size: 14px;
    font-weight: 600;
    color: var(--grey-0);
  }
`;

const EditorPane = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 150px;
  max-height: ${(props) => (props.$expanded ? "none" : "45vh")};
`;

/**
 * Scope element for a comparison pane. Carries the same inline font styles,
 * `indented` class and --sw-paragraph-spacing custom property as #editor, so
 * the shared typography rules render Option A/B exactly like the manuscript
 * (paragraph spacing, heading sizes, bullets) — same pattern as the Document
 * History preview.
 */
const ComparisonEditorContent = styled.div`
  ${editorTypographyStyles("&")}

  .ProseMirror {
    padding: 12px 24px;
    color: var(--grey-0);
    outline: none;
    min-height: 200px;
    cursor: default;
    caret-color: transparent;
  }
`;

const ScrollToTopBar = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 40px 24px;
`;

const FooterBar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 16px;
  gap: 8px;
  border-top: 1px solid ${themeVariables.divider};
  background: ${themeVariables.menuColorFullscreen};
`;

const DraftComparison = forwardRef(({ onPick, visible = true, isStreaming = false, editorComputedStyles }, ref) => {
  const paneARef = useRef(null);
  const paneBRef = useRef(null);
  const wrapperEndRef = useRef(null);
  const [expanded, setExpanded] = useState(false);

  // Mirrors what applyEditorComputedStyles sets on #editor so the shared
  // typography rules (paragraph-spacing var, indent-via-::before) resolve the
  // same way in both panes. Falls back to the editor defaults when the styles
  // haven't been computed yet.
  const contentStyle = {
    fontFamily: editorComputedStyles?.fontFamily || "Quattro, Courier New",
    fontSize: editorComputedStyles?.fontSize || "20px",
    lineHeight: editorComputedStyles?.lineHeight,
    "--sw-paragraph-spacing": editorComputedStyles?.paragraphSpacing || "0px",
  };
  const contentClassName = classNames({ indented: editorComputedStyles?.useTextIndent });

  const editorA = useEditor({
    extensions,
    editable: true,
    editorProps: {
      handleKeyDown: () => true,
      handlePaste: () => true,
      handleDrop: () => true,
    },
    content: "",
  });

  const editorB = useEditor({
    extensions,
    editable: true,
    editorProps: {
      handleKeyDown: () => true,
      handlePaste: () => true,
      handleDrop: () => true,
    },
    content: "",
  });

  useImperativeHandle(ref, () => ({
    editorA,
    editorB,
  }));

  const scrollToTop = useCallback((paneRef) => {
    paneRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const hasScrolledAfterStreamRef = useRef(false);

  useEffect(() => {
    if (isStreaming) {
      hasScrolledAfterStreamRef.current = false;
    }
  }, [isStreaming]);

  const doneStreaming = !isStreaming;

  useEffect(() => {
    if (visible && wrapperEndRef.current) {
      wrapperEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [visible]);

  useEffect(() => {
    if (doneStreaming && visible && !hasScrolledAfterStreamRef.current && wrapperEndRef.current) {
      hasScrolledAfterStreamRef.current = true;
      wrapperEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
      // After the "Back to top" buttons render inside each pane, scroll the
      // inner pane containers to the bottom so those buttons are visible.
      requestAnimationFrame(() => {
        [paneARef, paneBRef].forEach((ref) => {
          if (ref.current) {
            ref.current.scrollTo({ top: ref.current.scrollHeight, behavior: "smooth" });
          }
        });
      });
    }
  }, [doneStreaming, visible]);

  return (
    <ComparisonWrapper $visible={visible}>
      <PromptRow>
        <PromptMessage>
          We wrote this two ways, which do you prefer?{" "}
          <SudoTooltip
            text="We wrote two drafts, but only charged you credits for one. Your choice helps make Sudowrite better."
            position="bottom"
            triggerAs="span"
          >
            <TwoWays>Learn more.</TwoWays>
          </SudoTooltip>
        </PromptMessage>
        {doneStreaming && (
          <IconButton
            src={expanded ? "/assets/noun-minimize-1364472.svg" : "/assets/noun-expand-4983045.svg"}
            onClick={() => setExpanded((prev) => !prev)}
            ariaLabel={expanded ? "Collapse panels" : "Expand panels"}
            hoverTipText={expanded ? "Collapse Panels" : "Expand Panels"}
            size={28}
            iconSize={22}
          />
        )}
      </PromptRow>
      <ComparisonContainer>
        <Panel>
          <PanelHeader>
            <span className="label">Option A</span>
          </PanelHeader>
          <EditorPane
            ref={paneARef}
            $expanded={expanded}
          >
            <ComparisonEditorContent
              style={contentStyle}
              className={contentClassName}
            >
              <EditorContent editor={editorA} />
            </ComparisonEditorContent>
            {doneStreaming && !expanded && (
              <ScrollToTopBar>
                <SudoButton
                  variant="tertiaryGrey"
                  size="small"
                  onClick={() => scrollToTop(paneARef)}
                  aria-label="Scroll to top"
                >
                  ↑ Back to top
                </SudoButton>
              </ScrollToTopBar>
            )}
          </EditorPane>
          <FooterBar>
            <TooltipButton
              variant="primary"
              size="small"
              onClick={() => onPick(editorA, "A")}
              aria-label="Pick Option A"
              disabled={isStreaming}
              tooltip="Waiting for generation to complete."
              tooltipDisabled={!isStreaming}
            >
              Pick This One
            </TooltipButton>
          </FooterBar>
        </Panel>
        <Panel>
          <PanelHeader>
            <span className="label">Option B</span>
          </PanelHeader>
          <EditorPane
            ref={paneBRef}
            $expanded={expanded}
          >
            <ComparisonEditorContent
              style={contentStyle}
              className={contentClassName}
            >
              <EditorContent editor={editorB} />
            </ComparisonEditorContent>
            {doneStreaming && !expanded && (
              <ScrollToTopBar>
                <SudoButton
                  variant="tertiaryGrey"
                  size="small"
                  onClick={() => scrollToTop(paneBRef)}
                  aria-label="Scroll to top"
                >
                  ↑ Back to top
                </SudoButton>
              </ScrollToTopBar>
            )}
          </EditorPane>
          <FooterBar>
            <TooltipButton
              variant="primary"
              size="small"
              onClick={() => onPick(editorB, "B")}
              aria-label="Pick Option B"
              disabled={isStreaming}
              tooltip="Waiting for generation to complete."
              tooltipDisabled={!isStreaming}
            >
              Pick This One
            </TooltipButton>
          </FooterBar>
        </Panel>
      </ComparisonContainer>
      <div ref={wrapperEndRef} />
    </ComparisonWrapper>
  );
});

export default DraftComparison;
